import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useResendOtpMutation, useValidateSignUpMutation } from 'api/authApi';
import { REFERRAL_CODE } from 'constants/config';
import mixpanelEvents from 'constants/mixpanel';
import { Button, BUTTON_SIZE_TYPES, BUTTON_STATE_TYPES, BUTTON_TYPES } from 'destiny';
import { usePostLogin } from 'hooks/usePostLogin';
import { useQueryValue } from 'hooks/useQueryValue';
import { EMPTY_TOAST, SIGNUP_STEPS } from 'modules/auth/auth.constants';
import { SignUpInfoType } from 'modules/auth/auth.types';
import { ValidateSignInResponseType } from 'types/api';
import { trackMixpanel } from 'utils/mixpanel';
import { ButtonLoader } from 'components/loader/ButtonLoader';
import OtpInputWithResend from 'components/OtpInputWithResend';

interface SignUpOtpFormPropsTypes {
  signUpInfo: SignUpInfoType;
  setToast: Dispatch<SetStateAction<{ hidden: boolean; isError: boolean; message: string }>>;
  currentCapability: string;
  setCurrentStep: Dispatch<SetStateAction<SIGNUP_STEPS>>;
}

const SignUpOtpForm: FC<SignUpOtpFormPropsTypes> = ({ signUpInfo, setToast, currentCapability, setCurrentStep }) => {
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  const referralCode = useQueryValue(REFERRAL_CODE);

  const [resendOtp, { isLoading: isFetchingOtp }] = useResendOtpMutation();
  const [validateOtp, { isLoading: isOtpValidationLoading }] = useValidateSignUpMutation();

  const handlePostLogin = usePostLogin();

  const handleResendOtp = async () => {
    trackMixpanel(mixpanelEvents.SIGNUP_OTP_RESEND, { email: signUpInfo.email });

    try {
      setToast(EMPTY_TOAST);
      await resendOtp({
        token: signUpInfo.token,
      }).unwrap();
      setToast({
        hidden: false,
        isError: false,
        message: 'An OTP has been sent to your email',
      });
    } catch (e: any) {
      setToast({
        hidden: false,
        isError: true,
        message: e?.data?.error?.message || 'Some error occured',
      });
    }
  };

  const handleOtpSubmit = async () => {
    trackMixpanel(mixpanelEvents.SIGNUP_SUBMIT, { email: signUpInfo.email });

    setToast(EMPTY_TOAST);
    if (otp.replace(' ', '').length < 6) {
      setOtpError('Invalid OTP, please try again');
    } else {
      setOtpError('');
      try {
        const response: ValidateSignInResponseType = await validateOtp({
          token: signUpInfo?.token,
          first_name: signUpInfo?.firstName,
          last_name: signUpInfo?.lastName,
          merchant_name: signUpInfo.entityDetails[0].name,
          mobile_number: `${signUpInfo.phoneRegion?.value ?? ''}${signUpInfo.phone}`,
          entity_details: signUpInfo.entityDetails,
          otp: otp,
          capability_intent: [currentCapability],
          referral_code: referralCode,
        }).unwrap();

        trackMixpanel(mixpanelEvents.ALIAS, response);
        trackMixpanel(mixpanelEvents.SET_USER_PROPERTIES, response);

        if (response?.id) handlePostLogin(response.id);
      } catch (e: any) {
        setOtpError(e?.data?.error?.message || 'Some error occured');
      }
    }
  };

  const handleEditClick = () => {
    setOtpError('');
    setToast(EMPTY_TOAST);
    setCurrentStep(SIGNUP_STEPS.USER_DETAILS);
    trackMixpanel(mixpanelEvents.EDIT_EMAIL_CTA);
  };

  useEffect(() => {
    if (otp.replace(' ', '').length == 6) setOtpError('');
  }, [otp]);

  return (
    <>
      <OtpInputWithResend
        className='tw-mt-12'
        value={otp}
        error={otpError}
        onChange={(value: string) => setOtp(value)}
        onResend={handleResendOtp}
        onSubmit={handleOtpSubmit}
        title=''
        subTitle={
          <span className='f-14-300'>
            OTP sent to <strong>{signUpInfo?.email}</strong>
            <span className='f-14-500 tw-ml-2 tw-cursor-pointer tw-text-ZAMP_PRIMARY' onClick={handleEditClick}>
              Edit
            </span>
          </span>
        }
        hideMinuteOnZero={true}
      />
      <Button
        buttonProps={{
          size: BUTTON_SIZE_TYPES.MEDIUM,
          btnType: BUTTON_TYPES.PRIMARY,
          state: isOtpValidationLoading || isFetchingOtp ? BUTTON_STATE_TYPES.DISABLED : BUTTON_STATE_TYPES.DEFAULT,
          isLoading: isOtpValidationLoading || isFetchingOtp,
          wrapperClass: 'tw-mt-9 tw-w-36 tw-overflow-clip',
          onClick: handleOtpSubmit,
          id: 'SIGNUP_SUBMIT_OTP_BUTTON',
          eventCallback: trackMixpanel,
        }}
        loader={<ButtonLoader />}
      >
        Submit OTP
      </Button>
    </>
  );
};

export default SignUpOtpForm;
